import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCourse } from '@/contexts/CourseContext';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import ActivitiesStepShell from '@/components/step-shells/ActivitiesStepShell';

export default function Step3Activities() {
  const { course, updateCourse, generateActivities, createCheckpoint, loading, dispatch, resetChatHistory, welcomesSent, importMode } = useCourse();
  const [stepInitialized, setStepInitialized] = useState(false);
  const [generationStep, setGenerationStep] = useState('');

  // Initialize Step 3 when component mounts: clear chat once and trigger generation if needed
  useEffect(() => {
    const initializeStep3 = async () => {
      if (course && !stepInitialized && !welcomesSent?.[3]) {
        setStepInitialized(true);
        
        // In import mode, DON'T clear chat (welcome was pre-generated by button)
        // In regular mode, clear chat and generate activities if needed
        if (!importMode) {
          resetChatHistory();
          
          // Mark welcome as sent IMMEDIATELY to prevent duplicate calls
          dispatch({ type: 'SET_WELCOME_SENT', payload: 3 });
          
          // Generate activities automatically if none exist
          if (!course.topics || course.topics.length === 0) {
            await generateActivities(setGenerationStep);
          }
          
          // Save Step 2 checkpoint AFTER generation completes
          // (uses fresh session data, not stale DB data)
          await createCheckpoint('Checkpoint B', 2);

          // Build-mode welcome message (mirrors Step 2 pattern)
          // Use course.language from context - no need for extra API call
          const courseLanguage = course?.language || 'English';
          const isGreek = courseLanguage.toLowerCase() === 'greek' || courseLanguage.toLowerCase() === 'ελληνικά';

          const greetingMessage = isGreek
            ? "**Καλώς ήρθατε στο Βήμα 3!** Οι μαθησιακοί στόχοι έχουν οριστικοποιηθεί από το Βήμα 2.\n\nΤώρα θα δημιουργήσουμε δραστηριότητες μάθησης που θα ζωντανέψουν αυτούς τους στόχους. Έχω δημιουργήσει αρχικές δραστηριότητες συνδεδεμένες με τους στόχους σας.\n\nΡίξτε τους μια ματιά στο πλαϊνό πάνελ — μπορούμε να τις βελτιώσουμε μαζί ή να προσθέσουμε νέες.\n\n**Σε τι θα ήθελες να εστιάσεις πρώτα?**\n\nΌταν θέλετε να προχωρήσετε στο επόμενο βήμα, απλά ενημερωσέ με.\n\nΠώς θέλετε να προχωρήσουμε;"
            : "**Welcome to Step 3!** Your learning objectives have been finalized from Step 2.\n\nNow we'll create learning activities that bring those objectives to life. I've generated initial activities linked to your learning objectives and organized them into topics.\n\nTake a look at them in the panel — we can refine them together or add new ones.\n\n**What would you like to focus on first?**\n\nWhenever you're ready to move on to the next step, just ask me.\n\nHow would you like to proceed?";

          dispatch({ type: 'ADD_CHAT_MESSAGE', payload: {
            role: 'assistant',
            text: greetingMessage
          } });
        }
      }
    };
    initializeStep3();
  }, [course?.id, stepInitialized, welcomesSent, importMode, dispatch]);

  // REMOVED: This useEffect was causing duplicate API calls after every tool execution.
  // The welcome message is now handled only in the first useEffect (initializeStep3)
  // which properly guards against re-running with stepInitialized and welcomesSent state.

  const handleGenerate = async () => {
    await generateActivities(setGenerationStep);
  };

  const hasActivities = course && course.topics && course.topics.length > 0;

  const handleEdit = async (path, value) => {
    if (!course) return;
    const pathParts = path.split('.');
    const updatedCourse = JSON.parse(JSON.stringify(course));
    
    let current = updatedCourse;
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      const nextPart = pathParts[i + 1];
      const index = parseInt(nextPart);
      
      if (!isNaN(index)) {
        current = current[part][index];
        i++;
      } else {
        current = current[part];
      }
    }
    
    const lastKey = pathParts[pathParts.length - 1];
    current[lastKey] = value;
    
    await updateCourse(updatedCourse);
  };

  return (
    <ActivitiesStepShell
      topics={course?.topics || []}
      goals={course?.learningObjectives || []}
      onEdit={handleEdit}
    >
      {/* Generate button (when no activities) */}
      {!hasActivities && (
        <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-pink-100 text-center">
          <Button
            onClick={handleGenerate}
            disabled={loading}
            data-testid="generate-activities-button"
            className="py-6 px-8 text-lg font-bold bg-gradient-to-r from-pink-500 to-teal-500 hover:from-pink-600 hover:to-teal-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={24} />
                {generationStep || 'Generating Activities...'}
              </>
            ) : (
              <>Generate Activities with AI</>
            )}
          </Button>
        </div>
      )}
    </ActivitiesStepShell>
  );
}